import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import {A11yStatusProps} from '../shared/types';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(duration);
/**
 * Checks if a given string is a valid date in the format YYYY/MM/DD.
 *
 * Rules:
 *  - Year must start with 19 or 20 (i.e., between 1900 and 2099).
 *  - Month must be from 01 to 12.
 *  - Day must be from 01 to 31 (basic validation only, does not account for month length or leap years).
 *
 * @param newDateValue - The date string to validate (expected format: YYYY/MM/DD).
 * @returns {boolean} - Returns true if the string matches the date format, otherwise false.
 */
export const chekIfValidDate = (newDateValue: string): boolean => {
  const regex = /^(?:19|20)\d{2}\/(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])$/;
  if (!regex.test(newDateValue)) {
    return false;
  }
  return true;
};

export const getInitials = (userName: string): string => {
  if (!userName?.trim()) return '';
  const parts = userName.trim().split(/\s+/);
  const firstInitial = parts[0]?.charAt(0) || '';
  const lastInitial = parts[1]?.charAt(0) || '';

  return `${firstInitial}${lastInitial}`.toUpperCase();
};

/**
 * Formats an ISO date string into a human-readable format.
 *
 * @param dateString - The ISO date string to format (e.g., "2025-07-10T07:27:40.99").
 * @returns A formatted date string in the format **"YYYY/MM/DD HH:mm:ss"**
 *          (e.g., "2025/07/10 07:27:40"), or '—' if the input is invalid or missing.
 */
export const formatDateTime = (dateString?: string): string => {
  if (!dateString) return '—';

  const date = dayjs(dateString);
  if (!date.isValid()) return '—';

  return date.format('YYYY/MM/DD HH:mm:ss');
};

export const formatUTCDateTime = (dateString?: string): string => {
  if (!dateString) return '—';

  const date = dayjs.utc(dateString);
  if (!date.isValid()) return '—';

  return date.format('YYYY/MM/DD HH:mm:ss');
};

export const isValidName = (name: string): boolean => {
  // Allow only letters, numbers, and spaces (no underscores or hyphens)
  const regex = /^[a-zA-Z0-9 ]+$/;
  return regex.test(name.trim());
};

/**
 * Strips HTML tags from a string and returns plain text.
 *
 * @param htmlString - The HTML string to strip tags from.
 * @returns Plain text with HTML tags removed.
 */
export const stripHtmlTags = (htmlString: string): string => {
  if (!htmlString) return '';
  // Create a temporary DOM element to parse HTML
  const tmp = document.createElement('DIV');
  tmp.innerHTML = htmlString;
  // Return the text content without HTML tags
  return tmp.textContent || tmp.innerText || '';
};

export const getFileName = (str: string): string => {
  const fileNamePart = str.split(/[\\/]/).pop() || '';
  const regex = /^(.+)\.(?:xlsx|xls|csv)$/i;
  const result = regex.exec(fileNamePart);
  return result ? result[0] : '';
};

/**
 * Decodes HTML entities in a string to their corresponding characters.
 * Uses DOMParser for safer entity decoding without executing scripts.
 *
 * @param text - The text containing HTML entities (e.g., "&#39;hello&#39;", "&lt;div&gt;").
 * @returns Decoded text with HTML entities converted to characters (e.g., "'hello'", "<div>").
 */
export const decodeHtmlEntities = (text: string): string => {
  if (!text) return '';

  // Use DOMParser to safely decode HTML entities without executing scripts
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'text/html');
  return doc.documentElement.textContent || '';
};

/**
 * Formats a given date string into a specified format using Day.js.
 *
 * @param {string} [dateString] - The input date string to format (e.g. "2025-11-27 10:49:33.523").
 * @param {string} [format='YYYY/MM/DD'] - The desired output format (default: "YYYY/MM/DD").
 *
 * @returns {string} - The formatted date string, or "—" if the input is invalid or missing.
 *
 * @example
 * formatDateCommon("2025-11-27 10:49:33.523"); // "2025/11/27"
 * formatDateCommon("2025-11-27 10:49:33.523", "YYYY/MM/DD HH:mm:ss"); // "2025/11/27 10:49:33"
 * formatDateCommon(undefined); // "—"
 */
export const formatDateCommon = (
  dateString?: string,
  format: string = 'YYYY/MM/DD'
): string => {
  // Return a dash if no date is provided
  if (!dateString) return '—';

  const date = dayjs(dateString);

  // Return a dash if the parsed date is invalid
  if (!date.isValid()) return '—';

  // Format the valid date using the provided format
  return date.format(format);
};

export const isIntORBigInt = (value: unknown) => {
  return Number.isInteger(value) || typeof value === 'bigint';
};

export const safeParseNumber = (
  input: string,
  isInteger: boolean = false,
  length: number = 20
): {isValid: boolean; value: bigint | null; error?: string} => {
  // 1. Input sanitization (ReDoS defense)
  if (typeof input !== 'string') {
    return {isValid: false, value: null, error: 'Must be string'};
  }

  const trimmed = input.trim();
  if (trimmed.length === 0 || trimmed.length > length) {
    return {isValid: false, value: null, error: 'Invalid length'};
  }

  // 2. Fast pre-filter (simple, safe regex)
  if (!/^-?[\d.]*$/.test(trimmed)) {
    return {isValid: false, value: null, error: 'Invalid characters'};
  }

  // 3. Native parsing (fastest, most accurate)
  const num = BigInt(trimmed);
  if (Number.isNaN(num)) {
    return {isValid: false, value: null, error: 'Not a number'};
  }

  // 4. Integer validation
  if (isInteger && !isIntORBigInt(num)) {
    return {isValid: false, value: null, error: 'Must be integer'};
  }

  return {isValid: true, value: num};
};

export const generateRandomNumber = () => {
  const array = new Uint32Array(1);
  window.crypto.getRandomValues(array);
  return array[0] / (0xffffffff + 1);
};

export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const formatSecondsToDuration = (seconds: number = 0): string => {
  const durValue = dayjs.duration(seconds, 'seconds');
  return durValue.format('HH:mm:ss');
};

export const getRandomHexColor = (): string => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(generateRandomNumber() * 16)];
  }
  return color;
};

const timeReg = /^(\d+):([0-5]?\d):([0-5]?\d)$/;

export const validateTimeString = (timeStr: string) => {
  if (typeof timeStr !== 'string') return false;

  const match = timeReg.exec(timeStr);
  if (!match) return false;

  const [, hoursStr, minutesStr, secondsStr] = match;

  const minutes = Number(minutesStr);
  const seconds = Number(secondsStr);
  if (Number.isNaN(minutes) || Number.isNaN(seconds)) return false;
  if (minutes < 0 || minutes > 59) return false;
  if (seconds < 0 || seconds > 59) return false;

  const isHoursZero = /^0+$/.test(hoursStr);
  const isMinutesZero = /^0+$/.test(minutesStr);
  const isSecondsZero = /^0+$/.test(secondsStr);

  if (isHoursZero && isMinutesZero && isSecondsZero) return false;

  return true;
};

const parseTime = (timeStr: string) => {
  if (typeof timeStr !== 'string') return null;
  const match = timeReg.exec(timeStr);
  if (!match) return null;

  const [, hoursStr, minutesStr, secondsStr] = match;
  const hours = BigInt(hoursStr);
  const minutes = Number(minutesStr);
  const seconds = Number(secondsStr);

  if (Number.isNaN(minutes) || Number.isNaN(seconds)) return null;
  if (minutes < 0 || minutes > 59) return null;
  if (seconds < 0 || seconds > 59) return null;

  return {hours, minutes, seconds};
};

const compareParsedTimes = (a: any, b: any) => {
  if (a.hours < b.hours) return -1;
  if (a.hours > b.hours) return 1;
  if (a.minutes < b.minutes) return -1;
  if (a.minutes > b.minutes) return 1;
  if (a.seconds < b.seconds) return -1;
  if (a.seconds > b.seconds) return 1;
  return 0;
};

export const isTimeNotMoreThan = (timeA: string, timeB: string) => {
  const a = parseTime(timeA);
  const b = parseTime(timeB);
  if (!a || !b) return null;
  return compareParsedTimes(a, b) <= 0;
};

export const durationToSeconds = (duration: string = '00:00:00') => {
  const [hours, minutes, seconds] = duration.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

export const msToTime = (msStr?: string): string => {
  const parsed = Number(msStr?.trim());
  if (!Number.isFinite(parsed)) return '00:00:00';

  const ms = Math.max(0, parsed);
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const h = String(hours).padStart(2, '0');
  const m = String(minutes).padStart(2, '0');
  const s = String(seconds).padStart(2, '0');

  return `${h}:${m}:${s}`;
};

export const numberFormatter = new Intl.NumberFormat('en-US');

// Measure the space taken by a text
function measureTextFactory(font: string, letterSpacing = 0) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d')!;
  ctx.font = font;

  return (text: string) =>
    ctx.measureText(text).width + letterSpacing * (text.length - 1);
}

// Calculates the Truncated text along with comma separated strings , prefix and remaining count based on remaining space
export const getTruncatedLabelWithPrefix = (
  prefixText: string,
  items: string[],
  labelWidth: number,
  font = '14px Inter'
): string => {
  if (!items.length || labelWidth <= 0) {
    return prefixText;
  }
  const measureLabel = measureTextFactory(font, 0);

  const separator = ', ';
  const ellipsis = '…';
  const prefix = prefixText ? prefixText + ' ' : '';
  const visible: string[] = [];

  for (let i = 0; i < items.length; i++) {
    const word = items[i];

    const baseText =
      prefix + (visible.length ? visible.join(separator) + separator : '');

    // Try full word
    const fullText = baseText + word;
    if (measureLabel(fullText) <= labelWidth) {
      visible.push(word);
      continue;
    }

    // Try partial word
    let partial = '';

    for (let c = 0; c < word.length; c++) {
      const remainingAfterPartial = items.length - (i + 1);
      const countSuffix =
        remainingAfterPartial > 0 ? `+${remainingAfterPartial}` : '';

      const testText = baseText + partial + word[c] + ellipsis + countSuffix;

      if (measureLabel(testText) <= labelWidth) {
        partial += word[c];
      } else {
        break;
      }
    }

    // Partial fits
    if (partial) {
      const remaining = items.length - (i + 1);
      return (
        baseText + partial + ellipsis + (remaining > 0 ? `+${remaining}` : '')
      );
    }

    // No partial fits → full word hidden
    const hiddenCount = items.length - i;
    return baseText + `+${hiddenCount}`;
  }

  // Everything fits
  return prefix + visible.join(separator);
};

export const mapA11yStatusProps = (props?: A11yStatusProps) => {
  if (!props) return {};

  return {
    role: props.role,
    'aria-live': props.ariaLive,
    'aria-atomic': props.ariaAtomic,
    'aria-label': props.ariaLabel,
    'aria-labelledby': props.ariaLabelledBy,
    'aria-describedby': props.ariaDescribedBy,
    'aria-valuenow': props.ariaValueNow,
    'aria-valuemin': props.ariaValueMin,
    'aria-valuemax': props.ariaValueMax,
  };
};

export const clearLocalStorage = () => {
  const keep = new Set([
    'osano_consentmanager',
    'osano_consentmanager_uuid',
    'osano_consentmanager_tattles',
  ]);

  for (const storage of [localStorage]) {
    for (const key of Object.keys(storage)) {
      if (!keep.has(key)) {
        storage.removeItem(key);
      }
    }
  }
};

export const getCustomIndexName = (text: string): string => {
  const parts = text.split(':');

  return parts.length > 1 ? parts[1].trim().split(' ')[0] : '';
};
